import type { ParamMap } from '@angular/router';
import type { SearchFacetGroup } from '@entitylink/dynamics';
import type { ProviderCategory } from 'src/app/interfaces/provider-category';

export interface DirectoryListRoutePrefill {
  categoryLabel: string | null;
  areaLabel: string | null;
  providerCategoryId: number | null;
  /** When set, marketplace applies the matching Provider Category leaf chip. */
  providerCategoryChipLabel: string | null;
  /**
   * Taxonomy branch/root name — marketplace filters to orgs matching any leaf under this node.
   * Used by home Marketplace tiles (`?sfRoot=`).
   */
  serviceFocusRoot: string | null;
  source: string | null;
}

/**
 * Azure Search organisation field for Provider Categories (legacy name).
 * Prefer {@link DIRECTORY_PROVIDER_CATEGORY_FACET_KEY} in app code.
 * @see docs/technical-debt.md — rename index field + payload path
 */
export const DIRECTORY_VENDOR_SECTOR_FACET_KEY = 'VendorSectorCategories';

/** Marketplace Provider Categories facet key (value is still the legacy Azure field name). */
export const DIRECTORY_PROVIDER_CATEGORY_FACET_KEY = DIRECTORY_VENDOR_SECTOR_FACET_KEY;

/** Azure Search organisation field for company size (legacy name). Display as Provider Size. */
export const DIRECTORY_VENDOR_SIZE_FACET_KEY = 'VendorSize';

/** Buyer-facing marketplace facet title. Index keys stay legacy (`Vendor*`) until reindex. */
export function directoryFacetGroupLabel(key: string): string {
  if (key === DIRECTORY_PROVIDER_CATEGORY_FACET_KEY) {
    return 'Provider Categories';
  }
  if (key === DIRECTORY_VENDOR_SIZE_FACET_KEY) {
    return 'Provider Size';
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Selectable Provider Categories: leaf provider-category nodes (A–Z).
 * Orgs multi-select leaves; branch/root nodes are grouping only.
 */
export function directoryServiceFocusCategories(
  catalog: ProviderCategory[] | null | undefined,
): ProviderCategory[] {
  const rows = catalog ?? [];
  const parentIds = new Set(
    rows
      .map((row) => row.parentProviderCategoryId)
      .filter((id): id is number => id != null),
  );
  return [...rows]
    .filter((row) => !!row.name?.trim() && !parentIds.has(row.id))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** @deprecated Use {@link directoryServiceFocusCategories}. */
export function directoryServiceFocusRootCategories(
  catalog: ProviderCategory[] | null | undefined,
): ProviderCategory[] {
  return directoryServiceFocusCategories(catalog);
}

/** Leaf category names under a Provider Category branch/root (inclusive if the node is itself a leaf). */
export function directoryServiceFocusLeavesUnder(
  catalog: ProviderCategory[] | null | undefined,
  branchLabel: string,
): string[] {
  const wanted = normalizeDirectoryFacetLabel(branchLabel);
  if (!wanted) {
    return [];
  }

  const rows = catalog ?? [];
  const childrenByParent = new Map<number, ProviderCategory[]>();
  for (const row of rows) {
    if (row.parentProviderCategoryId == null) {
      continue;
    }
    const list = childrenByParent.get(row.parentProviderCategoryId) ?? [];
    list.push(row);
    childrenByParent.set(row.parentProviderCategoryId, list);
  }

  const node =
    rows.find(
      (row) =>
        row.parentProviderCategoryId == null
        && normalizeDirectoryFacetLabel(row.name) === wanted,
    )
    ?? rows.find((row) => normalizeDirectoryFacetLabel(row.name) === wanted);

  if (!node) {
    return [];
  }

  const leaves: string[] = [];
  const walk = (current: ProviderCategory): void => {
    const children = childrenByParent.get(current.id) ?? [];
    if (children.length === 0) {
      const name = current.name?.trim();
      if (name) {
        leaves.push(name);
      }
      return;
    }
    for (const child of children) {
      walk(child);
    }
  };
  walk(node);
  return leaves;
}

function parseIntQueryParam(params: ParamMap, key: string): number | null {
  const raw = params.get(key)?.trim();
  if (!raw) {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeDirectoryFacetLabel(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Legacy `/marketplace?category=…&from=orbit-explorer` + home `?sfRoot=` support — stripped after apply. */
export function parseDirectoryListRoutePrefill(params: ParamMap): DirectoryListRoutePrefill | null {
  const categoryLabel = params.get('category')?.trim() || params.get('pcat')?.trim() || null;
  const areaLabel = params.get('area')?.trim() || null;
  const providerCategoryId = parseIntQueryParam(params, 'vc');
  const serviceFocusRoot =
    params.get('sfRoot')?.trim()
    || params.get('serviceFocusRoot')?.trim()
    || null;
  const source = params.get('from')?.trim() || null;

  if (!categoryLabel && !areaLabel && providerCategoryId == null && !serviceFocusRoot) {
    return null;
  }

  return enrichDirectoryListPrefill({
    categoryLabel,
    areaLabel,
    providerCategoryId,
    providerCategoryChipLabel: null,
    serviceFocusRoot,
    source,
  });
}

export function enrichDirectoryListPrefill(
  prefill: DirectoryListRoutePrefill,
  catalog?: ProviderCategory[] | null,
): DirectoryListRoutePrefill {
  if (prefill.serviceFocusRoot?.trim()) {
    return { ...prefill, serviceFocusRoot: prefill.serviceFocusRoot.trim() };
  }

  const providerCategoryChipLabel =
    prefill.providerCategoryChipLabel
    ?? matchDirectoryProviderCategoryChipLabelById(prefill.providerCategoryId, catalog)
    ?? matchDirectoryProviderCategoryChipLabel(prefill.areaLabel, prefill.categoryLabel, catalog);

  return { ...prefill, providerCategoryChipLabel };
}

/** Resolve marketplace Provider Category chip label from taxonomy id (Orbit Explorer → marketplace). */
export function matchDirectoryProviderCategoryChipLabelById(
  providerCategoryId: number | null | undefined,
  catalog?: ProviderCategory[] | null,
): string | null {
  if (providerCategoryId == null || !Number.isFinite(providerCategoryId)) {
    return null;
  }
  const rows = catalog ?? [];
  const match = rows.find((row) => row.id === providerCategoryId);
  const name = match?.name?.trim();
  return name || null;
}

/** Best Provider Category chip for orbit directory labels (area / provider category name). */
export function matchDirectoryProviderCategoryChipLabel(
  areaLabel: string | null | undefined,
  categoryLabel: string | null | undefined,
  catalog?: ProviderCategory[] | null,
): string | null {
  const candidates = [areaLabel, categoryLabel].filter(
    (value): value is string => !!value?.trim(),
  );
  if (candidates.length === 0) {
    return null;
  }

  const chipLabels = directoryServiceFocusCategories(catalog).map((row) => row.name);
  if (chipLabels.length === 0) {
    return null;
  }

  let best: { chip: string; score: number } | null = null;
  for (const chip of chipLabels) {
    for (const candidate of candidates) {
      const score = directoryProviderCategoryChipMatchScore(chip, candidate);
      if (score > 0 && (!best || score > best.score)) {
        best = { chip, score };
      }
    }
  }

  return best?.chip ?? null;
}

/** @deprecated Use {@link matchDirectoryProviderCategoryChipLabel}. */
export function matchDirectoryPopularChipLabel(
  areaLabel: string | null | undefined,
  categoryLabel: string | null | undefined,
  catalog?: ProviderCategory[] | null,
): string | null {
  return matchDirectoryProviderCategoryChipLabel(areaLabel, categoryLabel, catalog);
}

function directoryProviderCategoryChipMatchScore(chipLabel: string, candidate: string): number {
  const chipNorm = normalizeDirectoryFacetLabel(chipLabel);
  const candidateNorm = normalizeDirectoryFacetLabel(candidate);
  if (!chipNorm || !candidateNorm) {
    return 0;
  }
  if (chipNorm === candidateNorm) {
    return 100;
  }
  if (candidateNorm.includes(chipNorm) || chipNorm.includes(candidateNorm)) {
    return 80;
  }

  const chipTokens = chipNorm.split(/[^a-z0-9]+/).filter(Boolean);
  const candidateTokens = candidateNorm.split(/[^a-z0-9]+/).filter(Boolean);
  if (chipTokens.length === 0 || candidateTokens.length === 0) {
    return 0;
  }

  const shared = chipTokens.filter((token) =>
    candidateTokens.some(
      (candidateToken) =>
        candidateToken === token
        || candidateToken.includes(token)
        || token.includes(candidateToken),
    ),
  );
  return shared.length > 0 ? 40 + shared.length * 10 : 0;
}

/** Whether a marketplace facet value matches a Provider Category chip label or orbit category label. */
export function directoryProviderCategoryMatchesLabel(
  categoryLabel: string,
  facetValue: string,
): boolean {
  const normalizedTarget = normalizeDirectoryFacetLabel(categoryLabel);
  const normalizedValue = normalizeDirectoryFacetLabel(facetValue);
  if (!normalizedTarget || !normalizedValue) {
    return false;
  }
  return (
    normalizedValue === normalizedTarget ||
    normalizedValue.includes(normalizedTarget) ||
    normalizedTarget.includes(normalizedValue)
  );
}

function providerCategoryFacetGroup(facetGroups: SearchFacetGroup[]): SearchFacetGroup | undefined {
  return facetGroups.find((group) => group.key === DIRECTORY_PROVIDER_CATEGORY_FACET_KEY);
}

/** Best Provider Categories index facet value for a category label, if the marketplace index exposes one. */
export function findDirectoryCategoryFacetMatch(
  facetGroups: SearchFacetGroup[],
  categoryLabel: string,
): string | null {
  const normalizedTarget = normalizeDirectoryFacetLabel(categoryLabel);
  if (!normalizedTarget) {
    return null;
  }

  const categoryGroup = providerCategoryFacetGroup(facetGroups);
  if (!categoryGroup) {
    return null;
  }

  for (const result of categoryGroup.results) {
    if (directoryProviderCategoryMatchesLabel(categoryLabel, result.value)) {
      return result.value;
    }
  }

  return null;
}

/**
 * Index facet value(s) for a Provider Category chip — exact category only.
 * Orgs can select many categories; filtering a chip means “has this category”, not a subtree.
 */
export function resolveDirectoryProviderCategoryFacetValues(
  facetGroups: SearchFacetGroup[],
  chipLabel: string,
  _catalog?: ProviderCategory[] | null,
): string[] {
  const label = chipLabel?.trim();
  if (!label) {
    return [];
  }

  const wanted = normalizeDirectoryFacetLabel(label);
  const categoryGroup = providerCategoryFacetGroup(facetGroups);
  const exact = categoryGroup?.results.find(
    (result) => normalizeDirectoryFacetLabel(result.value) === wanted,
  );
  return [exact?.value ?? label];
}

/** @deprecated Use {@link resolveDirectoryProviderCategoryFacetValues}. */
export function resolveDirectoryPopularChipFacetValues(
  facetGroups: SearchFacetGroup[],
  chipLabel: string,
  catalog?: ProviderCategory[] | null,
): string[] {
  return resolveDirectoryProviderCategoryFacetValues(facetGroups, chipLabel, catalog);
}

/** Organisations that selected this Provider Category (exact facet count). */
export function sumDirectoryProviderCategoryFacetCount(
  facetGroups: SearchFacetGroup[],
  chipLabel: string,
  _catalog?: ProviderCategory[] | null,
): number {
  const wanted = normalizeDirectoryFacetLabel(chipLabel);
  if (!wanted) {
    return 0;
  }

  const categoryGroup = providerCategoryFacetGroup(facetGroups);
  if (!categoryGroup) {
    return 0;
  }

  const hit = categoryGroup.results.find(
    (result) => normalizeDirectoryFacetLabel(result.value) === wanted,
  );
  return hit?.count ?? 0;
}

/** @deprecated Use {@link sumDirectoryProviderCategoryFacetCount}. */
export function sumDirectoryPopularChipFacetCount(
  facetGroups: SearchFacetGroup[],
  chipLabel: string,
  catalog?: ProviderCategory[] | null,
): number {
  return sumDirectoryProviderCategoryFacetCount(facetGroups, chipLabel, catalog);
}
